/**
 * Order Export
 *
 * A wide, download-first table: one row per ORDER DOCUMENT, so every shipment
 * carries its own tracking and delivery dates. That is deliberately not the
 * charge model the money reports use — a prepaid bundle is three shipments
 * here and one sale there, and both readings are correct for their question.
 *
 * The preview and the CSV run the identical query, so what is on screen is what
 * downloads. The preview is capped; the file is not.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import {
  Loader2, RefreshCw, AlertCircle, Search, X, Download, Table2, Info,
} from 'lucide-react'

import { billingReportsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'

const PREVIEW_ROWS = 100

function formatCell(value, type) {
  if (value === null || value === undefined || value === '') return '—'
  if (type === 'money') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    }).format(Number(value) / 100)
  }
  if (type === 'date') {
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    // ET, as the CSV writes it, whatever zone the viewer's browser is in.
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: 'America/New_York',
    })
  }
  return String(value)
}

function getDefaultDateRange() {
  return { from: subDays(new Date(), 29), to: new Date() }
}

export default function OrderExportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)

  const search = searchParams.get('q') || ''
  const [draft, setDraft] = useState(search)
  useEffect(() => { setDraft(search) }, [search])

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (!newRange?.from || !newRange?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(newRange.from, 'yyyy-MM-dd'))
      params.set('end', format(newRange.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const commitSearch = useCallback((value) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (value) params.set('q', value)
      else params.delete('q')
      return params
    })
  }, [setSearchParams])

  const queryParams = useMemo(() => ({
    startDate: format(dateRange.from, 'yyyy-MM-dd'),
    endDate: format(dateRange.to, 'yyyy-MM-dd'),
    search,
  }), [dateRange, search])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await billingReportsAPI.getOrderExport({ ...queryParams, limit: PREVIEW_ROWS })
      setData(response.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [queryParams])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  const handleDownload = async () => {
    try {
      setDownloading(true)
      setError(null)
      const response = await billingReportsAPI.downloadOrderExport(queryParams)
      // The filename is on the response, so the browser is told what to call
      // the file rather than saving it as the endpoint path.
      const disposition = response.headers['content-disposition'] || ''
      const named = disposition.match(/filename="?([^"]+)"?/)
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = named ? named[1] : `orders-${queryParams.startDate}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setDownloading(false)
    }
  }

  const columns = data?.columns || []
  const rows = data?.rows || []
  const total = data?.total || 0

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Order Export</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            One row per order, every shipment
            {data && ` · ${new Intl.NumberFormat('en-US').format(total)} in range`}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8 sm:h-9 sm:w-9">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button onClick={handleDownload} disabled={downloading || total === 0}>
            {downloading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            {downloading ? 'Building…' : 'Download CSV'}
          </Button>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); commitSearch(draft.trim()) }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Any column — email, name, order id, tracking, product…"
            className="w-full rounded-lg border border-input bg-background pl-9 pr-9 py-2 text-sm"
          />
          {draft && (
            <button
              type="button"
              onClick={() => { setDraft(''); commitSearch('') }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Button type="submit" variant="outline" size="sm">Search</Button>
      </form>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && !data.manifold_available && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Payment Status, Payment Type, Discount and the three Refunded columns come
            from Manifold, which is not reachable for this clinic — they are blank in
            both the preview and the file. Every other column is complete.
          </span>
        </div>
      )}

      {data?.truncated && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>This window is large enough that the oldest orders were left out. Narrow the dates.</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Table2 className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">
                {search ? `Nothing matching “${search}”.` : 'No orders in this range.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-background">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap border-b border-border"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.order_number || i} className="hover:bg-muted/40">
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={`px-3 py-2 whitespace-nowrap border-b border-border ${
                            col.type === 'money' ? 'text-right tabular-nums' : ''
                          } ${row[col.key] == null || row[col.key] === '' ? 'text-muted-foreground' : ''}`}
                        >
                          {formatCell(row[col.key], col.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {total > rows.length && (
        <p className="text-sm text-muted-foreground">
          Showing the first {new Intl.NumberFormat('en-US').format(rows.length)} of{' '}
          {new Intl.NumberFormat('en-US').format(total)}. The CSV contains all of them.
        </p>
      )}

      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          One row is one order document, so a prepaid bundle appears once per shipment
          with the price on the charged one and $0 on the rest. Money is exported in
          dollars; shipping is the flat $20 rate, not a per-order cost.
        </p>
        <p>
          Sales rep is recorded only when a rep sold through a pre-priced deeplink — a
          phone sale taken straight through checkout records nobody, so the column is
          usually blank. Quantity has no source field and is always blank. Billing
          Interval is read from the plan id, so plans named for the drug alone
          (“phentermine”) have none. Cancelled Amount is the order's own value, not the
          sum returned, because core stores no refund against a cancellation.
        </p>
      </div>
    </div>
  )
}
